import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import memoize from 'memoize-one';
import { useAbility } from '@casl/react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';
import { DataTableDT, DefaultModal, Icon, SimpleCenteredModal } from '../../components';

import { indexEmployeesRequest } from '../../requests/employees';
import { legalAttendanceReport } from '../../requests/legalreports';
import { AbilityContext } from '../../config/abilityContext';
import { AttendanceFilterDataTable } from '../../components/LegalReport';
import { downloadFile } from '../../services/utils';
import LegalReportFilterDt from './Filters/NewLegalReportFiltersDt';
import DtLegalReportFilters from './Filters/DtLegalReportFilters';
import ReportActions from './ReportActions';
import AttendancesPrint from './Print/AttendancesPrint';
import LegalReportsColumns from './LegalReportsColumns';
import AttendanceProofRender from '../../components/LegalReport/AttendanceProofRender';
import AbbreviationsGlossary from './AbbreviationsGlossary';

const customStyles = {
  rows: {
    style: {
      minHeight: '45px'
    }
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      borderLeft: '1px solid rgb(212, 212, 212)',
      borderRight: '1px solid rgb(212, 212, 212)',
      borderTop: '1px solid rgb(212, 212, 212)',
      borderBottom: '1px solid rgb(212, 212, 212)'
    }
  }
};

const recordsColumns = memoize(() => [
  {
    name: 'FECHA',
    selector: 'date',
    sortable: true,
    grow: '1'
  },
  {
    name: 'ASISTENCIA',
    selector: 'attendance',
    sortable: true,
    grow: '1'
  },
  {
    name: 'AUSENCIA',
    selector: 'absence',
    sortable: true,
    grow: '2'
  },
  {
    name: 'OBSERVACIONES',
    selector: 'justification',
    sortable: true,
    grow: '4',
    cell: row => <AttendanceProofRender item={row} />
  }
]);

const printTypeText = {
  print: '¿Estás seguro que quieres imprimir el reporte de asistencia?',
  all: '¿Estás seguro que quieres exportar el reporte de asistencia en formato '
};

const MyExpander = ({ data, dateRange, filterByHash }) => {
  const { id } = data;
  const customParams = useMemo(
    () => ({
      filter_by_employee: !filterByHash ? id : '',
      dateRange,
      filterByHash
    }),
    [id, dateRange, filterByHash]
  );
  return (
    <AttendanceFilterDataTable columns={recordsColumns} customParams={customParams} props customStyles={customStyles} />
  );
};

const AttendanceReportDataTable = ({
  currentEmployee,
  rrhh,
  filters,
  moreData: propMoreData,
  setMoreData: propSetMoreData,
  isBoss
}) => {
  const [onRequest, setOnRequest] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [modalState, setModalState] = useState({
    show: false,
    type: '',
    text: '',
    actionType: ''
  });
  const [modalShowGlosary, setModalShowGlosary] = useState(false);
  const [query, setQuery] = useState({});
  const [localMoreData, setLocalMoreData] = useState(false);
  const [amount, setAmount] = useState(0);
  const activeMoreData = typeof propMoreData !== 'undefined' ? propMoreData : localMoreData;
  const [dateRange, setDateRange] = useState([]);
  const ability = useAbility(AbilityContext);
  const [searchFirstStart, setSearchFirstStart] = useState(false);
  const dispatch = useDispatch();
  const [showFilter, setShowFilter] = useState(true);
  const [rowsDataPrint, setRowsDataPrint] = useState([]);
  const componentRef = useRef();
  const { user } = useSelector(state => state.auth);
  const [isSubmit, setIsSubmit] = useState(false);

  const handleClearRows = () => {
    setShowFilter(true);
    setFilteredEmployees([]);
  };

  const handleBeforePrint = () => {
    setOnRequest(false);
  };

  const printInfo = useReactToPrint({
    content: () => componentRef.current,
    onBeforePrint: handleBeforePrint
  });

  const isDownloableFile = format => {
    return ['.xlsx', '.docx', '.pdf'].includes(format);
  };

  const fetchEmployeeIds = params => {
    const adminOrDt = rrhh || user.role === 'dt';
    if (adminOrDt) {
      if (Number.isInteger(params.filter_employee_id)) return params.filter_employee_id;

      if (Array.isArray(params.filter_employee_ids) && params.filter_employee_ids.length > 0)
        return params.filter_employee_ids;
    }

    return !adminOrDt ? currentEmployee : '';
  };

  const handleAttendanceReportRequest = async (params, format, type = 'json') => {
    setOnRequest(true);
    setIsSubmit(true);
    const responseType = format === '.docx' ? 'blob' : type;
    legalAttendanceReport({
      dispatch,
      params: { ...params, filter_by_employee: fetchEmployeeIds(params), dateRange, paginate: false },
      format,
      responseType,
      successCallback: response => {
        if (isDownloableFile(format)) {
          downloadFile(response);
        }
        if (format === '.print') {
          setRowsDataPrint(response.data);
          printInfo();
        }
      },
      callback: () => {
        setOnRequest(false);
        setIsSubmit(false);
      }
    });
  };

  const handleRequest = async (params, format, responseType = 'json', currentFilters = {}) => {
    if (modalState.show) return;
    const allParams = { ...params, ...currentFilters };
    setOnRequest(true);
    indexEmployeesRequest({
      dispatch,
      params: {
        display_start: 0,
        display_length: 15,
        ...allParams,
        filter_employee_ids: fetchEmployeeIds(allParams),
        legal_reports: true,
        is_dt: false,
        sort_direction: 'asc',
        sort_column: 'name'
      },
      format,
      responseType,
      successCallback: response => {
        const { data } = response;
        setAmount(data.metadata.amount);
        setFilteredEmployees(data.data);
      },
      callback: () => setOnRequest(false)
    });
  };

  const handleOptionSelection = (action, actionType) => {
    switch (true) {
      case ['download_excel', '.xlsx'].includes(action):
        setModalState({ text: `${printTypeText.all} Excel?`, type: '.xlsx', show: true, actionType });
        break;
      case ['download_docx', '.docx'].includes(action):
        setModalState({ text: `${printTypeText.all} Word?`, type: '.docx', show: true, actionType });
        break;
      case ['download_pdf', '.pdf'].includes(action):
        setModalState({ text: `${printTypeText.all} Pdf?`, type: '.pdf', show: true, actionType });
        break;
      case ['print', '.print'].includes(action):
        setModalState({ text: printTypeText.print, type: '.print', show: true, actionType });
        break;
      default:
    }
  };

  const handleFilters = params => {
    setQuery({ ...params });
    setDateRange(params.range_date_workday);
    if (['.docx', '.xlsx', '.pdf', '.print'].includes(params.type)) {
      handleOptionSelection(params.type, 'download');
      return;
    }
    setSearchFirstStart(true);
    if (typeof propSetMoreData === 'function') propSetMoreData(!propMoreData);
    else setLocalMoreData(prev => !prev);
    setShowFilter(params.type === '.print');
  };

  const handleMassAction = actionType => {
    handleOptionSelection(actionType, 'preview');
  };

  const handleRawFilters = () => {
    if (!filters || Object.keys(filters).length === 0) return;

    // set local query and dateRange similar to handleFilters
    setQuery({ ...filters });
    setDateRange(filters.range_date_workday || []);

    if (['.docx', '.xlsx', '.pdf', '.print'].includes(filters.type)) {
      handleOptionSelection(filters.type, 'download');
      return;
    }

    setSearchFirstStart(true);
    if (typeof propSetMoreData === 'function') propSetMoreData(!propMoreData);
    else setLocalMoreData(prev => !prev);
    setShowFilter(filters.type === '.print');

    // Trigger the data request with the provided filters
    handleRequest(filters, '', '', filters);
  };

  useEffect(handleRawFilters, [filters]);

  return (
    <>
      <div className={showFilter ? '' : 'd-none'}>
        {user.role === 'dt' ? (
          <DtLegalReportFilters
            isArial
            formRequest={handleFilters}
            setModalShowGlosary={setModalShowGlosary}
            filterRRHH={rrhh}
            currentEmployee={currentEmployee}
            isSubmit={isSubmit}
            onRequest={onRequest}
          />
        ) : (
          <LegalReportFilterDt
            isArial
            formRequest={handleFilters}
            setModalShowGlosary={setModalShowGlosary}
            filterRRHH={rrhh}
            currentEmployee={currentEmployee}
            isSubmit={isSubmit}
            onRequest={onRequest}
          />
        )}
      </div>
      <div className={showFilter ? 'd-none' : 'attendance_report'}>
        {!isBoss && (
          <Row>
            <Col md={6} className="mt-3">
              <Button variant="primary" className="dt-report-btn" onClick={handleClearRows}>
                <Icon icon="chevron-back" width={15} />
                <span className="arial-dt">Volver al filtro</span>
              </Button>
            </Col>
          </Row>
        )}
        {!showFilter && <AbbreviationsGlossary />}
        <DataTableDT
          onRequest={onRequest}
          columns={LegalReportsColumns(user.role === 'dt')}
          data={filteredEmployees}
          totalRows={amount}
          moreData={activeMoreData}
          withMassActions={ability.can('index', 'AttendanceReport')}
          massActions={<ReportActions disabled={onRequest} handleClick={handleMassAction} dates={dateRange} isArial />}
          expandableRows
          expandableRowsComponent={<MyExpander dateRange={dateRange} filterByHash={query.filter_by_hash || ''} />}
          expandableRowExpanded={() => true}
          resourceRequest={params => handleRequest(params, '', '', query)}
          searchFirstStart={searchFirstStart}
          noDataComponentText="NO HAY TRABAJADORES QUE COINCIDAN CON LA SELECCIÓN"
        />
      </div>
      <DefaultModal
        title={modalState.type === '.print' ? 'Imprimir Reporte de Asistencias' : 'Exportar Reporte de Asistencias'}
        body={modalState.text}
        show={modalState.show}
        handleClose={() => setModalState(oldState => ({ ...oldState, show: false }))}
        handleConfirm={() => {
          handleAttendanceReportRequest(query, modalState.type, 'text');
          setModalState(oldState => ({ ...oldState, show: false }));
        }}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
      />
      <SimpleCenteredModal
        title="Glosario de Abreviaciones"
        body={
          <Card>
            <Card.Body>
              <Col md={12} className="mb-5">
                <Row>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">A.I</p> Ausencia
                    Injustificada
                  </Col>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">A.J</p> Ausencia
                    Justificada
                  </Col>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">A.T</p> Atraso
                  </Col>
                  <Col md={4}>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">D.E.J</p>{' '}
                    Distribucion Excepcional de Jornada
                  </Col>
                </Row>
              </Col>
              <Col md={12} className="mb-5">
                <Row>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">H.E</p> Horas
                    Extraordinarias
                  </Col>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">J.O</p> Jornada
                    Ordinaria
                  </Col>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">L.M</p> Licencia
                    Medica
                  </Col>
                  <Col md={4}>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">P.G.R</p> Permiso
                    con goce de Remuneraciones
                  </Col>
                </Row>
              </Col>
              <Col md={12} className="mb-5">
                <Row>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">C.T</p> Cambio de
                    Turno
                  </Col>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">PREN</p> Prenatal
                  </Col>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">POSTN</p>{' '}
                    Postnatal
                  </Col>
                  <Col md={4}>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">P.S.G.R</p>{' '}
                    Permiso sin goce de Remuneraciones
                  </Col>
                </Row>
              </Col>
              <Col md={12} className="mb-5">
                <Row>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">VAC</p> Vacaciones
                  </Col>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-primary rounded-circle text-white text-center">S.A</p> Salida
                    Anticipada
                  </Col>
                  <Col>
                    <p className="d-inline p-2 w-25 bg-warning rounded-circle text-white text-center">. . .</p>{' '}
                    Marcaciones Agregadas
                  </Col>
                  <Col md={4}>
                    <p className="d-inline p-2 w-25 bg-success rounded-circle text-white text-center">. . .</p>{' '}
                    Marcaciones Modificadas
                  </Col>
                </Row>
              </Col>
            </Card.Body>
          </Card>
        }
        size="xl"
        show={modalShowGlosary}
        onHide={() => setModalShowGlosary(false)}
      />
      <div style={{ display: 'none' }}>
        <AttendancesPrint
          ref={componentRef}
          headers={recordsColumns()}
          rows={rowsDataPrint}
          customStyles={customStyles}
        />
      </div>
    </>
  );
};

export default AttendanceReportDataTable;
